import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'

interface ThreadPos {
  threadId: string
  messageAt: number
}

import { logout } from '~/auth/slices/auth.slice'
import { RootState } from '~/redux/store'
interface ChatState {
  isChatOpen: boolean
  openThreadId: string | null
  prevThreadId: string | null
  threadPos: ThreadPos | null
  newMessage: string
  transitionDidFinish: boolean
  showChatInfoBox: boolean
  chatNotification: boolean
  lastMessage: string
  lastMessageSentAt: number | null
}

const initialState: ChatState = {
  isChatOpen: false,
  openThreadId: null,
  prevThreadId: null,
  threadPos: null,
  newMessage: '',
  transitionDidFinish: true,
  showChatInfoBox: true,
  chatNotification: false,
  lastMessage: '',
  lastMessageSentAt: null,
}

const slice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    openThread(state, { payload }: PayloadAction<string | null>) {
      state.openThreadId = payload
      state.isChatOpen = Boolean(payload)
    },
    toggleOpenThread(state, { payload }: PayloadAction<ThreadPos | null>) {
      if (payload) {
        state.lastMessageSentAt = payload.messageAt

        if (payload.threadId === state.prevThreadId) {
          state.isChatOpen = !state.isChatOpen
          state.openThreadId = state.isChatOpen ? payload.threadId : null
        } else {
          if (!state.isChatOpen) {
            state.isChatOpen = true
          }
          state.openThreadId = payload.threadId
          state.prevThreadId = payload.threadId
        }
      }

      state.threadPos = payload
    },
    dismissChatInfoBox: (state) => ({
      ...state,
      showChatInfoBox: false,
    }),

    turnNotificationON: (state) => ({
      ...state,
      chatNotification: true,
    }),

    turnNotificationOFF: (state) => ({
      ...state,
      chatNotification: false,
    }),

    updateLastMessage: (state, { payload }: PayloadAction<{ message: string; messageSentAt: number }>) => ({
      ...state,
      lastMessage: payload.message,
      lastMessageSentAt: payload.messageSentAt,
    }),

    setIsOpen: (state, action: PayloadAction<boolean>) => ({
      ...state,
      isChatOpen: action.payload,
      openThreadId: action.payload ? state.openThreadId : null,
    }),

    setNewMessage: (state, action: PayloadAction<string>) => ({
      ...state,
      newMessage: action.payload,
    }),

    setTransitionDidFinish: (state, action: PayloadAction<boolean>) => ({
      ...state,
      transitionDidFinish: action.payload,
    }),
  },
  extraReducers: (builder) => {
    builder.addCase(logout, () => {
      return initialState
    })
  },
})

export const selectOpenThreadId = (state: RootState): ChatState['openThreadId'] => state.chat.openThreadId
export const threadPos = (state: RootState): ChatState['threadPos'] => state.chat.threadPos

export const selectThreadPos = createSelector([threadPos], (pos) =>
  pos ? { ...pos, messageAt: new Date(pos.messageAt) } : null,
)

export const selectShowChatInfoBox = (state: RootState): boolean => state.chat.showChatInfoBox
export const selectChatNotification = (state: RootState): boolean => state.chat.chatNotification
export const selectChatLastMessage = (state: RootState): string => state.chat.lastMessage
export const selectIsChatOpen = (state: RootState): ChatState['isChatOpen'] => state.chat.isChatOpen
export const SelectNewMessage = (state: RootState): ChatState['newMessage'] => state.chat.newMessage
export const selectTransitionDidFinish = (state: RootState): ChatState['transitionDidFinish'] =>
  state.chat.transitionDidFinish
export const selectChatLastMessageSentAt = (state: RootState): ChatState['lastMessageSentAt'] =>
  state.chat.lastMessageSentAt

// ACTIONS
export const {
  openThread,
  dismissChatInfoBox,
  turnNotificationOFF,
  turnNotificationON,
  updateLastMessage,
  setIsOpen: setIsOpenActionCreator,
  setNewMessage: setNewMessageActionCreator,
  setTransitionDidFinish: setTransitionDidFinishActionCreator,
  setThreadPos,
  toggleOpenThread,
} = slice.actions

export default slice.reducer
